import { c as _c } from "react/compiler-runtime";
import { useCallback, useRef, useState } from "react";

/**
 * Configuration options for the long press hook.
 */
export interface LongPressOptions {
  /** Duration in milliseconds to consider as a long press */
  delay?: number;
  /** Should prevent default behavior on long press */
  shouldPreventDefault?: boolean;
  /** Should prevent propagation of the long press event */
  shouldStopPropagation?: boolean;
}
export interface LongPressResult<T = Element> {
  /** Props to spread on target element */
  handlerProps: {
    onMouseDown: (e: React.MouseEvent<T>) => void;
    onMouseUp: (e: React.MouseEvent<T>) => void;
    onMouseLeave: (e: React.MouseEvent<T>) => void;
    onTouchStart: (e: React.TouchEvent<T>) => void;
    onTouchEnd: (e: React.TouchEvent<T>) => void;
  };
  /** Whether a long press is currently in progress */
  isLongPressing: boolean;
}

/**
 * Detects long press events for touch and mouse interactions.
 *
 * @param onLongPress - Callback function to execute on long press
 * @param onClick - Optional callback function to execute on normal click/press
 * @param options - Configuration options for the hook
 * @returns Object containing event handlers to attach to an element
 */
export function useLongPress(onLongPress, onClick, t0) {
  const $ = _c(30);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    delay: t2,
    shouldPreventDefault: t3,
    shouldStopPropagation: t4
  } = t1;
  const delay = t2 === undefined ? 500 : t2;
  const shouldPreventDefault = t3 === undefined ? true : t3;
  const shouldStopPropagation = t4 === undefined ? false : t4;
  const [isLongPressing, setIsLongPressing] = useState(false);
  const timerRef = useRef(null);
  const targetRef = useRef(null);
  const eventRef = useRef(null);
  let t5;
  if ($[2] !== delay || $[3] !== onLongPress || $[4] !== shouldPreventDefault || $[5] !== shouldStopPropagation) {
    t5 = e => {
      if (shouldPreventDefault) {
        e.preventDefault();
      }
      if (shouldStopPropagation) {
        e.stopPropagation();
      }
      targetRef.current = e.target;
      eventRef.current = e;
      timerRef.current = setTimeout(() => {
        if (eventRef.current) {
          setIsLongPressing(true);
          onLongPress(eventRef.current);
        }
      }, delay);
    };
    $[2] = delay;
    $[3] = onLongPress;
    $[4] = shouldPreventDefault;
    $[5] = shouldStopPropagation;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const start = t5;
  let t6;
  if ($[7] !== isLongPressing || $[8] !== onClick || $[9] !== shouldPreventDefault || $[10] !== shouldStopPropagation) {
    t6 = (e_0, t7) => {
      const shouldTriggerClick = t7 === undefined ? false : t7;
      if (shouldPreventDefault) {
        e_0.preventDefault();
      }
      if (shouldStopPropagation) {
        e_0.stopPropagation();
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (shouldTriggerClick && eventRef.current && !isLongPressing && onClick) {
        onClick(eventRef.current);
      }
      setIsLongPressing(false);
      targetRef.current = null;
      eventRef.current = null;
    };
    $[7] = isLongPressing;
    $[8] = onClick;
    $[9] = shouldPreventDefault;
    $[10] = shouldStopPropagation;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const clear = t6;
  let t7;
  if ($[12] !== start) {
    t7 = e_1 => start(e_1);
    $[12] = start;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  let t9;
  if ($[14] !== clear) {
    t8 = e_2 => clear(e_2, true);
    t9 = e_3 => clear(e_3);
    $[14] = clear;
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  let t10;
  if ($[17] !== start) {
    t10 = e_4 => start(e_4);
    $[17] = start;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== clear) {
    t11 = e_5 => clear(e_5, true);
    $[19] = clear;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t10 || $[22] !== t11 || $[23] !== t7 || $[24] !== t8 || $[25] !== t9) {
    t12 = {
      onMouseDown: t7,
      onMouseUp: t8,
      onMouseLeave: t9,
      onTouchStart: t10,
      onTouchEnd: t11
    };
    $[21] = t10;
    $[22] = t11;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const handlerProps = t12;
  let t13;
  if ($[27] !== handlerProps || $[28] !== isLongPressing) {
    t13 = {
      handlerProps,
      isLongPressing
    };
    $[27] = handlerProps;
    $[28] = isLongPressing;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
}